import request from 'api/request';
import {handleError, formatter} from 'helpers';
import {MAX_NUM_SUGGESTIONS, Sorting} from 'helpers/constants';

import * as alerts from './alertActions';
import reloadInventory from './inventoryActions';
import {
  inventoryItemActionTypes,
  priceListItemActionTypes,
} from './actionTypes';

export const inventoryItemsLoading = () => ({
  type: inventoryItemActionTypes.INVENTORY_ITEM_LOADING,
});

const urlWithParams = (baseUrl, searchCriteria, selectedFilter) => {
  let url = baseUrl;
  if (searchCriteria) {
    url += `&name=${searchCriteria}`;
  }
  if (selectedFilter) {
    if (selectedFilter.value === 'ShowDeleted') {
      url += '&showInactive=true';
    } else if (selectedFilter.value === 'ShowLowInventory') {
      url += '&lowInventory=true';
    } else {
      url += '&showInactive=false';
    }
  } else {
    url += '&showInactive=false';
  }
  return url;
};

const getInventoryItemsListItems = ({
  actionType = inventoryItemActionTypes.GET_INVENTORY_ITEM_SUCCESS,
  pageInfo,
  sortInfo,
  searchCriteria,
  selectedFilter,
}) => dispatch => {
  dispatch(inventoryItemsLoading);

  const {pageNumber, pageSize} = pageInfo;
  const {sortColumn, sortOrder} = sortInfo;

  let url = `/inventoryitems?&pageSize=${pageSize}&rowIndex=${(pageNumber - 1) *
    pageSize}`;

  if (sortColumn) {
    const sortCamelCase = `${sortColumn
      .charAt(0)
      .toLowerCase()}${sortColumn.substring(1)}`;
    const apiSortOrder = sortOrder === 'Descending' ? '-' : '';

    url += `&sort=${apiSortOrder}${sortCamelCase}`;
    if (sortCamelCase !== 'qualifiedName') {
      url += ',qualifiedName';
    }
  }

  return request({url: urlWithParams(url, searchCriteria, selectedFilter)})
    .then(data => {
      const results =
        data && data.data && data.data.results ? data.data.results : [];
      const totalRows =
        data && data.data && data.data.totalRows ? data.data.totalRows : 0;

      dispatch({
        type: actionType,
        items: results.map(x => ({
          ...x,
          formattedOnHand: formatter.formatNumber(x.quantityOnHand),
          rate: formatter.formatRate(x.defaultSellPrice || 0),
          itemType: 'Product',
          formattedDescription: formatter.formatDescription(x.description),
        })),
        totalRows,
      });
    })
    .catch(error => {
      handleError('Unable to load Inventory Items', error, dispatch);
    });
};

export const getInventoryItems = ({
  actionType = inventoryItemActionTypes.GET_INVENTORY_ITEM_SUCCESS,
  pageInfo = {},
  sortInfo = {},
  searchCriteria = '',
  selectedFilter,
}) => dispatch => {
  dispatch(
    getInventoryItemsListItems({
      actionType,
      pageInfo,
      sortInfo,
      searchCriteria,
      selectedFilter,
    }),
  );
};

export const loadInventoryItems = (dispatch, getState) => {
  const state = getState();
  const {inventoryItems} = state;
  dispatch(
    getInventoryItems({
      pageInfo: inventoryItems.pageInfo,
      sortInfo: inventoryItems.sortInfo,
      searchCriteria: inventoryItems.searchCriteria,
      selectedFilter: inventoryItems.selectedFilter,
    }),
  );
};

// Paging
const pageChanged = ({pageNumber, pageSize}) => ({
  type: inventoryItemActionTypes.INVENTORY_ITEM_PAGE_CHANGED,
  pageNumber,
  pageSize,
});

export const changePage = (
  {pageNumber, pageSize},
  sortInfo,
  searchCriteria,
  selectedFilter,
) => dispatch => {
  dispatch(pageChanged({pageNumber, pageSize}));

  return dispatch(
    getInventoryItemsListItems({
      pageInfo: {pageNumber, pageSize},
      sortInfo,
      searchCriteria,
      selectedFilter,
    }),
  );
};

// Sort
const changeSortValue = ({sortColumn, sortOrder}) => ({
  type: inventoryItemActionTypes.INVENTORY_ITEM_SORT_CHANGED,
  sortColumn,
  sortOrder,
});

export const changeSort = (
  pageInfo,
  {sortColumn, sortOrder},
  searchCriteria,
  selectedFilter,
) => dispatch => {
  const isSortValueProvided = sortColumn && sortColumn.length > 0;

  if (isSortValueProvided === false) {
    throw new Error(
      'Unable to search Inventory Items when no sort has been provided but is required.',
    );
  }

  dispatch(changeSortValue({sortColumn, sortOrder}));

  return dispatch(
    getInventoryItemsListItems({
      pageInfo,
      sortInfo: {sortColumn, sortOrder},
      searchCriteria,
      selectedFilter,
    }),
  );
};

// Searching
export const searchChanged = (
  pageInfo,
  sortInfo,
  searchCriteria,
  selectedFilter,
) => dispatch => {
  dispatch({
    type: inventoryItemActionTypes.INVENTORY_ITEM_SEARCH,
    searchCriteria,
  });

  return dispatch(
    getInventoryItemsListItems({
      pageInfo,
      sortInfo,
      searchCriteria,
      selectedFilter,
    }),
  );
};

export const changeFilter = filterValue => (dispatch, getState) => {
  const state = getState();
  const {inventoryItems} = state;
  dispatch(
    getInventoryItems({
      actionType: inventoryItemActionTypes.GET_PREVIEW_LIST_SUCCESS,
      pageInfo: {pageNumber: 1, pageSize: MAX_NUM_SUGGESTIONS},
      sortInfo: {sortColumn: 'name', sortOrder: Sorting.asc},
      searchCriteria: filterValue,
      selectedFilter: inventoryItems.selectedFilter,
    }),
  );
};

// Filter
export const filterChanged = (
  pageInfo,
  sortInfo,
  searchCriteria,
  selectedFilter,
) => dispatch => {
  dispatch({
    type: inventoryItemActionTypes.INVENTORY_ITEM_FILTER_CHANGED,
    selectedFilter,
  });

  return dispatch(
    getInventoryItemsListItems({
      pageInfo,
      sortInfo,
      searchCriteria,
      selectedFilter,
    }),
  );
};

// Select All Checkbox
export const updateAllChecked = isChecked => dispatch =>
  dispatch({
    type: inventoryItemActionTypes.INVENTORY_ITEM_ALL_CHECK_CHANGED,
    isChecked,
  });

// Item Checkbox
export const updateItemChecked = itemId => dispatch =>
  dispatch({
    type: inventoryItemActionTypes.INVENTORY_ITEM_CHECK_CHANGED,
    itemId,
  });

// Open Item Locations Table
export const openItemLocationsTable = itemId => dispatch => {
  dispatch({
    type: inventoryItemActionTypes.INVENTORY_ITEM_OPEN_LOCATIONS_TABLE,
    itemId,
  });
  return request({
    url: `/inventoryitems/${itemId}/locations`,
  })
    .then(data => {
      const results =
        data && data.data && data.data.results ? data.data.results : [];
      dispatch({
        type:
          inventoryItemActionTypes.GET_INVENTORY_ITEM_LOCATIONS_TABLE_SUCCESS,
        itemId,
        itemLocations: results,
      });
    })
    .catch(error => {
      handleError('Unable to load Inventory Locations', error, dispatch);
    });
};

// Close Item Locations Table
export const closeItemLocationsTable = itemId => dispatch =>
  dispatch({
    type: inventoryItemActionTypes.INVENTORY_ITEM_CLOSE_LOCATIONS_TABLE,
    itemId,
  });

// Sort Item Locations Table
export const sortItemLocationsTable = (
  itemId,
  sortColumn,
  sortOrder,
) => dispatch =>
  dispatch({
    type: inventoryItemActionTypes.INVENTORY_ITEM_LOCATIONS_TABLE_SORT_CHANGED,
    itemId,
    sortOrder,
    sortColumn,
  });

// Submit Editable Column
export const changeItemLocationInventory = ({
  itemId,
  itemLocation,
  property,
  value,
  fromEditItemPage = false,
  stockProduct = false,
}) => (dispatch, getState) => {
  if (
    (property === 'reOrderPoint' || property === 'quantityOnHand') &&
    value.toString().length > 13
  ) {
    handleError(
      'Unable to update inventory item',
      {message: 'The value must be less than 13 characters long.'},
      dispatch,
    );

    return Promise.reject();
  }

  const body = {
    id: itemLocation ? itemLocation.id : 0,
    reOrderPoint: itemLocation ? itemLocation.reOrderPoint : 0,
    quantityOnHand: itemLocation ? itemLocation.quantityOnHand : 0,
  };

  if (property) {
    body[property] = value;
  }

  const url = `/inventoryitems/${itemId}/locations/${
    itemLocation.inventoryLocationId
  }`;
  let type =
    inventoryItemActionTypes.INVENTORY_ITEM_LOCATIONS_TABLE_ITEM_SUBMIT;

  if (fromEditItemPage) {
    type = priceListItemActionTypes.PRICE_LIST_ITEM_INVENTORY_CHANGED;
  } else if (stockProduct) {
    type = inventoryItemActionTypes.INVENTORY_ITEM_STOCK_PRODUCT_SUBMIT;
  }

  return request({
    url,
    method: itemLocation && itemLocation.id ? 'PUT' : 'POST',
    body,
  })
    .then(result => {
      if (result.status === 422) {
        throw new Error(result.data);
      }

      dispatch({
        type,
        itemLocation: result.data,
        property,
        value,
      });
      reloadInventory(dispatch, getState, {
        doLoadInventoryItems: fromEditItemPage || stockProduct,
      });

      return result;
    })
    .catch(error => {
      handleError('Unable to update inventory item', error, dispatch);
      return false;
    });
};

// Delete
export const deleteItems = (items, callback) => (dispatch, getState) => {
  return request({
    url: `/InventoryItems`,
    method: 'DELETE',
    body: items.map(x => x.partId),
  })
    .then(() => {
      dispatch(alerts.success('Successfully deleted'));
      reloadInventory(dispatch, getState);
      callback();
    })
    .catch(error => {
      handleError('Unable to delete inventory item', error, dispatch);
    });
};

export const exportInventoryCSV = (
  searchCriteria,
  selectedFilter,
) => dispatch => {
  return request({
    url: urlWithParams('/CSV/InventoryItems?', searchCriteria, selectedFilter),
    headers: {contentType: 'text/csv'},
  })
    .then(result => {
      if (result.status === 200) {
        dispatch(alerts.success(result.data.message));
        return true;
      }
      return false;
    })
    .catch(error => handleError('Error exporting to CSV', error, dispatch));
};



// WEBPACK FOOTER //
// ./src/actions/inventoryItemActions.js